import type {
  ClientSearchItem,
  EquipmentDetails,
  EquipmentHistoryItem,
  EquipmentListItem,
  EquipmentMeta,
  EquipmentSort,
  EquipmentWaiting,
  HelpdeskCounters,
  Paginated,
  ProductionSummary,
  TicketDetails,
  TicketListItem,
} from '@erp/shared';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from './api';

/**
 * Производство и база оборудования — запросы с ключом ['equipment'], заявки — ['helpdesk'].
 * Realtime-событие equipment.changed перечитывает оба.
 */

export const equipmentMetaQuery = queryOptions({
  queryKey: ['equipment', 'meta'],
  queryFn: () => api<EquipmentMeta>('/equipment/meta'),
  staleTime: 60_000,
});

export interface EquipmentFilter {
  lifecycle?: 'production' | 'in_service' | 'decommissioned' | 'all';
  board?: boolean;
  modelId?: string;
  contactId?: string;
  companyId?: string;
  dealId?: string;
  q?: string;
  waiting?: EquipmentWaiting;
  sort?: EquipmentSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

export const equipmentListQuery = (f: EquipmentFilter) =>
  queryOptions({
    queryKey: ['equipment', 'list', f],
    queryFn: () =>
      api<Paginated<EquipmentListItem>>('/equipment', {
        query: { ...f, pageSize: f.board ? 500 : 50 } as never,
      }),
    placeholderData: keepPreviousData,
  });

export const productionSummaryQuery = queryOptions({
  queryKey: ['equipment', 'summary'],
  queryFn: () => api<ProductionSummary[]>('/equipment/summary'),
});

export const equipmentQuery = (id: string) =>
  queryOptions({
    queryKey: ['equipment', 'item', id],
    queryFn: () => api<EquipmentDetails>(`/equipment/${id}`),
  });

export const equipmentHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['equipment', 'item', id, 'history'],
    queryFn: () => api<EquipmentHistoryItem[]>(`/equipment/${id}/history`),
  });

export interface TicketsFilter {
  scope?: 'all' | 'mine' | 'free' | 'created';
  state?: 'open' | 'overdue' | 'done' | 'all';
  equipmentId?: string;
  contactId?: string;
  companyId?: string;
  q?: string;
  page?: number;
}

export const ticketsQuery = (f: TicketsFilter) =>
  queryOptions({
    queryKey: ['helpdesk', 'tickets', f],
    queryFn: () =>
      api<Paginated<TicketListItem>>('/helpdesk/tickets', { query: { ...f, pageSize: 50 } as never }),
    placeholderData: keepPreviousData,
  });

export const ticketQuery = (id: string) =>
  queryOptions({
    queryKey: ['helpdesk', 'ticket', id],
    queryFn: () => api<TicketDetails>(`/helpdesk/tickets/${id}`),
  });

export const ticketHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['helpdesk', 'ticket', id, 'history'],
    queryFn: () => api<EquipmentHistoryItem[]>(`/helpdesk/tickets/${id}/history`),
  });

export const helpdeskCountersQuery = queryOptions({
  queryKey: ['helpdesk', 'counters'],
  queryFn: () => api<HelpdeskCounters>('/helpdesk/counters'),
  staleTime: 30_000,
  refetchInterval: 120_000,
});

export const fetchClients = (q: string) => api<ClientSearchItem[]>('/equipment/clients', { query: { q } });

export const fetchEquipment = (q: string) =>
  api<Paginated<EquipmentListItem>>('/equipment', { query: { q, pageSize: 20 } }).then((r) => r.items);
